/**
 * Guía: Creadores de Contenido.
 *
 * Contenido derivado del capítulo **15** del manual: §0 (el código permanente
 * y el vínculo de por vida), §4 (los seis estados, que son las seis
 * pestañas), §5 (el asistente de activación y sus tres pasos), §6 (las
 * tarifas con vigencia) y §7 (qué corta suspender de verdad).
 *
 * Los dos pasos con más consecuencia son el del código —único y permanente,
 * es la palabra que el creador va a decir en sus vídeos— y el de las
 * tarifas, que no son un campo sino ventanas temporales.
 */

import type { TourFlow } from '../types';

const CREADORES_PATH = '/content-creators';

export const c15Creadores: TourFlow = {
  id: 'admin.content-creators',
  chapter: '15',
  title: 'Creadores de contenido',
  summary:
    'De la solicitud al código: por qué es permanente, cómo funcionan las tarifas con vigencia y qué corta suspender.',
  href: CREADORES_PATH,
  requiresCountry: true,
  estimatedMin: 5,
  steps: [
    {
      id: 'intro',
      title: 'Un cliente con código propio',
      body:
        'Un creador es un cliente de la app al que se le aprueba un código único. Cuando ' +
        'otro cliente lo captura queda vinculado a él **de por vida**, y cada compra que ' +
        'haga genera comisión para el creador.',
      route: CREADORES_PATH,
    },
    {
      id: 'six-tabs',
      title: 'Las pestañas son los seis estados',
      body:
        'Pendientes, en revisión y rechazadas son solicitudes que llegan desde la app. ' +
        'Activos, pausados y suspendidos ya son creadores. Cada una con su contador: la ' +
        'cola de trabajo son las pendientes.',
      target: 'admin.creators.tabs',
      placement: 'bottom',
      optionalTarget: true,
    },
    {
      id: 'code-is-forever',
      title: 'El código es único y no se cambia',
      body:
        'Es la palabra que el creador va a decir en sus vídeos, así que se elige una vez. ' +
        'El asistente comprueba mientras escribes si está tomado y no deja avanzar si lo ' +
        'está. Piénsalo con él antes de aprobar.',
    },
    {
      id: 'wizard',
      title: 'Aprobar decide de una vez su identidad y su economía',
      body:
        'Tres pasos: sus datos y el porcentaje, su imagen pública, y su cuenta. El ' +
        'porcentaje viene con la banda permitida cargada del servidor, no escrita en la ' +
        'pantalla — así no se desfasa cuando cambia la política.',
    },
    {
      id: 'uploads-on-confirm',
      title: 'Los adjuntos se suben al confirmar',
      body:
        'Elegir una imagen no la sube: se ve en local y viaja al guardar. Por eso cancelar ' +
        'el asistente a mitad no deja nada tirado, y por eso el último paso tarda un poco ' +
        'más.',
    },
    {
      id: 'rates-are-windows',
      title: 'Las tarifas no son un campo: son ventanas',
      body:
        'Cada tarifa vale desde una fecha hasta otra, y no pueden solaparse. Programar una ' +
        'hacia el futuro es lo normal —una campaña de fin de año— y al crearla se cierra ' +
        'sola la anterior. Los huecos sin tarifa se pintan en rojo: ahí el motor caería a ' +
        'un valor de respaldo.',
    },
    {
      id: 'no-backdating',
      title: 'Una tarifa hacia atrás no recalcula nada',
      body:
        'Las comisiones ya emitidas guardan el porcentaje con el que se calcularon. Poner ' +
        'una tarifa con fecha pasada sólo ensucia el histórico, así que está bloqueada ' +
        'salvo que se pida expresamente. Y revocar sólo vale para tarifas que aún no ' +
        'empezaron: una vigente o pasada es evidencia contable.',
    },
    {
      id: 'suspend-cuts-money',
      title: 'Suspender sí le corta el dinero',
      body:
        'Deja de captar supporters, su perfil público se oculta y **sus supporters actuales ' +
        'dejan de generarle comisión**. No se pierde lo anterior: lo ya generado entra en ' +
        'la primera liquidación tras reactivarlo. Pausar es lo mismo sin sanción, y suele ' +
        'pedirlo el propio creador.',
    },
    {
      id: 'link-belongs-to-customer',
      title: 'El vínculo con un cliente se corrige en su ficha',
      body:
        'Desde aquí no: se hace desde la ficha del cliente, y existe para cuando escribió ' +
        'mal el código. Mover el vínculo mueve la atribución de sus compras futuras.',
    },
  ],
};
